import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useForm } from 'react-hook-form'
import routes from '../../config/routes'
import { useAuth } from '../../providers/AuthProvider'
import clubService from '../../services/club.service'
import { ClubEntry, ClubEntryStatus } from '../../types/club/ClubEntry'
import { HeroIcons } from '../../lib/utils/heroIcons'

interface StatusMessageProps {
  status: ClubEntryStatus
  t: (key: string) => string
}

function StatusMessage({ status, t }: StatusMessageProps) {
  const getStatusConfig = () => {
    switch (status) {
      case ClubEntryStatus.REQUESTED:
        return {
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          textColor: 'text-amber-800',
          titleKey: 'requestInvitation.form.status.requested.title',
          messageKey: 'requestInvitation.form.status.requested.message',
          icon: <HeroIcons.ClockIcon className="h-5 w-5 shrink-0" />,
        }
      case ClubEntryStatus.INVITED:
        return {
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-800',
          titleKey: 'requestInvitation.form.status.invited.title',
          messageKey: 'requestInvitation.form.status.invited.message',
          icon: <HeroIcons.EnvelopeIcon className="h-5 w-5 shrink-0" />,
        }
      case ClubEntryStatus.ACCEPTED:
        return {
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          textColor: 'text-green-800',
          titleKey: 'requestInvitation.form.status.accepted.title',
          messageKey: 'requestInvitation.form.status.accepted.message',
          icon: <HeroIcons.CheckCircleIcon className="h-5 w-5 shrink-0" />,
        }
      case ClubEntryStatus.REFUSED:
        return {
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-800',
          titleKey: 'requestInvitation.form.status.refused.title',
          messageKey: 'requestInvitation.form.status.refused.message',
          icon: (
            <HeroIcons.ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
          ),
        }
      case ClubEntryStatus.REMOVED:
        return {
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-800',
          titleKey: 'requestInvitation.form.status.removed.title',
          messageKey: 'requestInvitation.form.status.removed.message',
          icon: <HeroIcons.XCircleIcon className="h-5 w-5 shrink-0" />,
        }
      case ClubEntryStatus.COMPLETED:
        return {
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          textColor: 'text-green-800',
          titleKey: 'requestInvitation.form.status.completed.title',
          messageKey: 'requestInvitation.form.status.completed.message',
          icon: <HeroIcons.CheckBadgeIcon className="h-5 w-5 shrink-0" />,
        }
      case ClubEntryStatus.EXPIRED:
        return {
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          textColor: 'text-gray-800',
          titleKey: 'requestInvitation.form.status.expired.title',
          messageKey: 'requestInvitation.form.status.expired.message',
          icon: (
            <HeroIcons.ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
          ),
        }
      default:
        return null
    }
  }

  const config = getStatusConfig()

  if (!config) return null

  return (
    <div
      className={`w-full rounded-md border ${config.borderColor} ${config.bgColor} px-4 py-4 text-left text-sm ${config.textColor}`}
    >
      <div className="mb-1 flex items-center gap-2">
        {config.icon}
        <p className="font-semibold">{t(config.titleKey)}</p>
      </div>
      <p className="pl-7 text-sm">{t(config.messageKey)}</p>
    </div>
  )
}

type RequestInfoFormInputs = {
  amount: number
}

interface RequestInvitationFormProps {
  clubId: string
  propertyId?: string
}

export default function RequestInvitationForm({
  clubId,
  propertyId,
}: RequestInvitationFormProps) {
  const { t } = useTranslation(['club', 'common'])
  const router = useRouter()
  const { isLoggedIn, isLoading } = useAuth()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RequestInfoFormInputs>()

  const [loading, setLoading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isClubEntryLoading, setIsClubEntryLoading] = useState<boolean>(false)
  const [clubEntry, setClubEntry] = useState<ClubEntry | null>(null)

  const canSubmitNewRequest = clubEntry?.status === ClubEntryStatus.EXPIRED
  const isFormDisabled = loading

  const fetchClubEntry = useCallback(async () => {
    if (isLoggedIn && !isLoading) {
      try {
        setIsClubEntryLoading(true)
        const _clubEntry = await clubService.getMeClubEntry(clubId)
        setClubEntry(_clubEntry)
      } catch (error) {
        setClubEntry(null)
      } finally {
        setIsClubEntryLoading(false)
      }
    }
  }, [clubId, isLoading, isLoggedIn])

  useEffect(() => {
    fetchClubEntry()
  }, [fetchClubEntry])

  const redirectToInvestmentPage = async (): Promise<boolean> => {
    if (!propertyId) {
      return false
    }

    await router.replace(
      {
        pathname: routes.property,
        query: { propertyId, clubAccepted: '1' },
      },
      undefined,
      {
        locale: router.locale,
        scroll: false,
      },
    )

    return true
  }

  const onSubmit = async (values: RequestInfoFormInputs) => {
    if (!isLoggedIn) {
      await router.push({
        pathname: routes.login,
        query: { redirect: router.asPath || router.pathname },
      })
      return
    }

    try {
      setSubmitError(null)
      setLoading(true)

      const payload = {
        ...values,
        amount: Number(values.amount) * 100,
      }

      const clubEntry = await clubService.requestInvitation(
        clubId as string,
        payload,
      )

      if (clubEntry.status === ClubEntryStatus.ACCEPTED) {
        const hasRedirected = await redirectToInvestmentPage()
        if (hasRedirected) {
          return
        }
      }

      setClubEntry(clubEntry)
    } catch (error) {
      const apiMessage = (error as any)?.response?.data?.message
      setSubmitError(
        Array.isArray(apiMessage)
          ? apiMessage[0]
          : apiMessage || t('requestInvitation.form.errors.other'),
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex grow flex-col justify-center p-6 text-left sm:p-8 lg:p-10">
      <div className="mb-6">
        <p className="mb-3 inline-flex items-center gap-2 rounded-full bg-brandLightBlue/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brandDarkPurple">
          <HeroIcons.ShieldCheckIcon className="h-4 w-4" />
          {t('requestInvitation.eyebrow')}
        </p>
        <h2 className="mb-3 text-3xl font-semibold leading-tight text-gray-950">
          {t('requestInvitation.title')}
        </h2>
        <p className="max-w-xl text-base leading-7 text-gray-600">
          {t('requestInvitation.text1')}
        </p>
      </div>

      {submitError && (
        <div className="mb-4 flex w-full items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          <HeroIcons.ExclamationCircleIcon className="mt-0.5 h-5 w-5 shrink-0" />
          <p>{submitError}</p>
        </div>
      )}

      {isClubEntryLoading && (
        <div className="w-full rounded-md border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-3 text-sm text-gray-700">
            <HeroIcons.ArrowPathIcon className="h-5 w-5 animate-spin text-brandLightBlue" />
            <span>{t('requestInvitation.form.loadingStatus')}</span>
          </div>
        </div>
      )}

      {!isClubEntryLoading && clubEntry?.status !== undefined && (
        <StatusMessage status={clubEntry.status} t={t} />
      )}

      {!isClubEntryLoading && (!clubEntry || canSubmitNewRequest) && (
        <>
          {!isLoggedIn && (
            <div className="w-full space-y-4 rounded-md border border-gray-200 bg-gray-50 px-5 py-5 text-sm text-gray-800 shadow-sm">
              <p className="text-base font-semibold text-gray-900">
                {t('requestInvitation.form.auth.message')}
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Link
                  href={{
                    pathname: routes.login,
                    query: { redirect: router.asPath || router.pathname },
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-gray-950 px-4 py-3 text-center font-medium text-white transition hover:opacity-90"
                >
                  <HeroIcons.ArrowRightOnRectangleIcon className="h-5 w-5" />
                  {t('requestInvitation.form.auth.login')}
                </Link>
                <Link
                  href={routes.register}
                  className="flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-3 text-center font-medium text-gray-900 transition hover:bg-gray-100"
                >
                  <HeroIcons.UserPlusIcon className="h-5 w-5" />
                  {t('requestInvitation.form.auth.register')}
                </Link>
              </div>
            </div>
          )}

          {isLoggedIn && (
            <form
              className="w-full space-y-5 rounded-md border border-gray-200 bg-white p-5 shadow-sm"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-900">
                  {t('requestInvitation.form.amount')}
                </label>
                <div className="relative">
                  <HeroIcons.CurrencyEuroIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="number"
                    step="1"
                    disabled={isFormDisabled}
                    placeholder={t('requestInvitation.form.amountPlaceholder')}
                    {...register('amount', {
                      required: t(
                        'requestInvitation.form.errors.amountRequired',
                      ) as string,
                      min: {
                        value: 10,
                        message: t('requestInvitation.form.errors.amountMin'),
                      },
                    })}
                    className="w-full rounded-md border border-gray-300 bg-white py-3 pl-10 pr-3 text-base text-gray-950 outline-none transition placeholder:text-gray-400 focus:border-brandLightBlue focus:ring-2 focus:ring-brandLightBlue/20 disabled:bg-gray-100 disabled:opacity-60"
                  />
                </div>
                {errors.amount && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.amount.message}
                  </p>
                )}
                <p className="mt-2 text-xs leading-5 text-gray-500">
                  {t('requestInvitation.form.amountHint')}
                </p>
              </div>

              <button
                type="submit"
                disabled={isFormDisabled}
                className="flex w-full items-center justify-center gap-2 rounded-md bg-brandLightBlue px-4 py-3 font-medium text-white transition hover:bg-brandDarkBlue disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <svg
                      className="h-5 w-5 animate-spin text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                    <span>{t('loading.inProgress', { ns: 'common' })}</span>
                  </div>
                ) : (
                  <>
                    <HeroIcons.PaperAirplaneIcon className="h-5 w-5" />
                    {t('requestInvitation.form.submitButton')}
                  </>
                )}
              </button>
            </form>
          )}
          <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('requestInvitation.legalTitle')}
            </p>
            <p className="whitespace-pre-line text-xs leading-5 text-gray-600">
              {t('requestInvitation.text2')}
            </p>
          </div>
        </>
      )}
    </div>
  )
}
